/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/react-hooks";
import { Segment, Form, Button, Accordion } from "semantic-ui-react";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { reportStatistics } from "./queries";
import { ReportParams } from ".";
import { authCheckCallApi, getUrlParam } from "../../Authenticator/utils";
import styles from "../MyAccount.module.css";
import BuildsList from "./BuildsList";
import useForm from "react-hook-form";
import cStyles from "./../../Components.module.css";
import ConfirmationDialog from "../../ConfirmationDialog";
import { Ico } from "../../Icon/Icon";
import { defaultEndDate, defaultStartDate } from "./Reports";

const Builds: React.FC = () => {
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [startDate, setStartDate] = useState<Date>(defaultStartDate());
  const [endDate, setEndDate] = useState<Date>(defaultEndDate());
  const [loading, setLoading] = useState<boolean>(false);
  const [showById, setShowById] = useState<boolean>(false);
  const [reportData, setReportData] = useState<any>();
  const [findBuildId, setFindBuildId] = useState<string | null>(null);
  const { register, handleSubmit, errors, setValue, getValues } =
    useForm<FormData>();
  const uuidValidator =
    /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
  type FormData = {
    buildId: string;
  };
  const emptyInputs = {
    buildId: "",
  };
  const client = useApolloClient();
  const [inputs, setInputs] = useState<FormData>(emptyInputs);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  useEffect(() => {
    const buildParam = getUrlParam("b");
    if (buildParam) {
      setShowById(true);
      setValue("buildId", buildParam);
      setFindBuildId(buildParam);
    }
  }, []);

  const searchBuilds = async (fixedStartDate?: Date, fixedEndDate?: Date) => {
    setLoading(true);
    startDate.setHours(0);
    startDate.setMinutes(0);
    authCheckCallApi(() => {
      client
        .query({
          fetchPolicy: "no-cache",
          query: reportStatistics,
          variables: {
            options: {
              start: fixedStartDate || startDate,
              end: fixedEndDate || endDate,
              reportType: "aps-statistics",
            },
          },
        })
        .then((result) => {
          let allBuilds: any[] = [];
          Object.values(JSON.parse(result.data.res.result)).forEach(
            (buildsGroup: any) => {
              allBuilds = [...allBuilds, ...buildsGroup];
            }
          );
          setReportData(allBuilds);
        })
        .catch((e) => {
          setMessage({
            title: "Search Builds",
            message: e.message,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const getBuildArtifactsById = () => {
    setFindBuildId(getValues().buildId);
  };

  const searchById = () => {
    return (
      <Accordion style={{ marginTop: "1rem" }}>
        <Accordion.Title
          className={styles.advancedTitle}
          active={showById}
          index={1}
          onClick={() => {
            setShowById(!showById);
          }}
        >
          <Ico
            name={showById === true ? "down" : "right"}
            className={styles.accordionIcon}
          />
          Search by Build ID
        </Accordion.Title>
        <Accordion.Content active={showById}>
          <Form
            onSubmit={handleSubmit(getBuildArtifactsById)}
            style={{ marginBottom: "3rem" }}
          >
            <Form.Group inline>
              <Form.Field required error={!!errors.buildId}>
                <input
                  style={{ width: "30rem" }}
                  autoComplete="off"
                  name="buildId"
                  key="buildId"
                  ref={register({ pattern: uuidValidator })}
                  onChange={handleInputChange}
                  defaultValue={inputs.buildId}
                  placeholder="Build ID"
                  className={cStyles.input}
                />
              </Form.Field>

              <Button
                primary
                className={cStyles.buttonPrime}
                type="submit"
                disabled={loading}
              >
                <Ico name="search" style={{ marginRight: "10px" }} />
                Search by ID
              </Button>
            </Form.Group>
          </Form>
        </Accordion.Content>
      </Accordion>
    );
  };

  return (
    <Segment basic>
      <ReportParams
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        loading={loading}
        generateReport={searchBuilds}
        title="Search Builds"
      />

      {searchById()}

      <BuildsList
        buildsData={reportData}
        findBuildId={findBuildId}
        setFindBuildId={setFindBuildId}
        setMessage={setMessage}
      />

      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </Segment>
  );
};

export default Builds;
